import type React from 'react';

import type { Destination } from '@elara/client-core/models/DestinationModel';

import type { IconName } from '#ui/MaterialIcon.js';

import { useEffect, useMemo } from 'react';

import { formatTimeInTimezone } from '@elara/client-core/lib/date-utils';
import { getEmojiFlag } from '@elara/client-core/lib/utils';
import { mapWeatherConditionToMaterialIcon } from '@elara/domain/weather';

import { useChatInputState, useDestinationGuideState, useTripsState } from '#states/hooks.js';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '#ui/accordion.js';
import { AskElaraButton } from '#ui/AskElaraButton.js';
import { Card, CardContent, CardHeader, CardTitle } from '#ui/card.js';
import { SimpleMarkdown } from '#ui/chat/SimpleMarkdown.js';
import Loader from '#ui/loader.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { ShortlistButton } from '#ui/shortlist-button.js';
import { WeatherForecast } from '#ui/weather-forecast.js';

import { GuideLayout } from './GuideLayout.js';
import { GuideNotFound } from './GuideNotFound.js';
import { PlanTripCTA } from './PlanTripCTA.js';

import { MapWidget } from '../chat/index.js';

const WEATHER_FORECAST_THRESHOLD_DAYS = 7;

interface DestinationGuideProps {
  destinationId?: string;
}

/** The travel-tip fields `DestinationTips` renders; the public shared itinerary sends just these. */
export type DestinationTipsLogistics = Pick<
  NonNullable<Destination['logistics']>,
  'tippingCulture' | 'accessibility' | 'emergencyNumbers' | 'powerOutletInfo' | 'travel_advisories'
>;

export function DestinationTips({ destination }: { destination: { logistics?: DestinationTipsLogistics } }) {
  const logistics = destination.logistics;
  const emergencyNumbers = logistics?.emergencyNumbers;

  const hasDifferentEmergencyNumbers =
    new Set([emergencyNumbers?.police, emergencyNumbers?.medical, emergencyNumbers?.fire].filter(Boolean)).size > 1;

  // One list of sections feeds both renderings: the accordion on screen, and a flat, fully expanded
  // list on paper — a closed accordion has no content in the DOM, and these are travel essentials.
  const sections: Array<{ value: string; icon: IconName; title: string; content: React.ReactNode }> = [
    ...(logistics?.tippingCulture
      ? [
          {
            value: 'tipping-culture',
            icon: 'send_money' as const,
            title: 'Tipping culture',
            content: <SimpleMarkdown text={logistics.tippingCulture} />,
          },
        ]
      : []),
    ...(logistics?.accessibility
      ? [
          {
            value: 'accessibility',
            icon: 'directions_bus' as const,
            title: 'Accessibility & transport',
            content: <SimpleMarkdown text={logistics.accessibility} />,
          },
        ]
      : []),
    ...(emergencyNumbers
      ? [
          {
            value: 'emergency-numbers',
            icon: 'emergency' as const,
            title: 'Emergency numbers',
            content: hasDifferentEmergencyNumbers ? (
              <>
                <div>Police: {emergencyNumbers.police}</div>
                <div>Medical: {emergencyNumbers.medical}</div>
                <div>Fire: {emergencyNumbers.fire}</div>
              </>
            ) : (
              <div>{emergencyNumbers.police}</div>
            ),
          },
        ]
      : []),
    ...(logistics?.powerOutletInfo
      ? [
          {
            value: 'power-outlet-info',
            icon: 'power' as const,
            title: 'Power outlets',
            content: (
              <>
                <div>Type {logistics.powerOutletInfo.type.join(', ')}</div>
                <div>Voltage {logistics.powerOutletInfo.voltage}</div>
                <div>Frequency {logistics.powerOutletInfo.frequency}</div>
              </>
            ),
          },
        ]
      : []),
    ...(logistics?.travel_advisories
      ? [
          {
            value: 'travel-advisories',
            icon: 'shield' as const,
            title: 'Travel advisories',
            content: <SimpleMarkdown text={logistics.travel_advisories} />,
          },
        ]
      : []),
  ];

  return (
    <>
      <Accordion type="multiple" className="w-full print:hidden">
        {sections.map((section) => (
          <AccordionItem key={section.value} value={section.value}>
            <AccordionTrigger>
              <div className="flex items-center gap-3">
                <MaterialIcon icon={section.icon} /> {section.title}
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-4 pt-0">{section.content}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="hidden print:flex flex-col gap-3 py-3 text-sm">
        {sections.map((section) => (
          <div key={section.value} className="flex flex-col gap-1 break-inside-avoid">
            <div className="flex items-center gap-2 font-medium">
              <MaterialIcon icon={section.icon} size={IconSizes.xs} /> {section.title}
            </div>
            <div className="text-pretty">{section.content}</div>
          </div>
        ))}
      </div>
    </>
  );
}

export function DestinationGuide({ destinationId }: DestinationGuideProps) {
  const destinationGuideState = useDestinationGuideState();
  const chatInputState = useChatInputState();
  const tripsState = useTripsState();

  useEffect(() => {
    destinationGuideState.reset();

    if (destinationId) {
      destinationGuideState.findDestination(destinationId);
    }
    // destinationGuideState is a stable reference from DI container, no need to include in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationId]);

  const destination = destinationGuideState.destination;

  // Preferred units based on locale: US/LR/MM use imperial, else metric
  const preferredUnits = useMemo<'metric' | 'imperial'>(() => {
    const lang = navigator.language || '';
    const parts = lang.split('-');
    const region = parts.length > 1 ? parts[1] : undefined;
    return region && ['US', 'LR', 'MM'].includes(region) ? 'imperial' : 'metric';
  }, []);

  // Load current weather + 5-day forecast once destination is ready
  useEffect(() => {
    if (!destination) return;
    const toIsoDate = (d: Date) => d.toISOString().split('T')[0]!;
    const today = new Date();
    const end = new Date(today);
    end.setDate(end.getDate() + 4);
    void destinationGuideState.loadWeather({
      units: preferredUnits,
      startDate: toIsoDate(today),
      endDate: toIsoDate(end),
    });
    // destinationGuideState is a stable reference from DI container, no need to include in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, preferredUnits]);

  const startDate = tripsState.current?.startDate;

  const showWeatherForecast = useMemo(() => {
    if (!startDate) return false;

    try {
      const start = new Date(startDate);
      if (Number.isNaN(start.getTime())) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0); // Normalize to start of day
      start.setHours(0, 0, 0, 0);

      const daysDiff = Math.floor((start.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return daysDiff >= 0 && daysDiff <= WEATHER_FORECAST_THRESHOLD_DAYS;
    } catch {
      return false;
    }
  }, [startDate]);

  if (!destinationId) {
    return <GuideNotFound guide="destination" title="Destination not found" />;
  }

  if (destinationGuideState.isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader loading={true} />
      </div>
    );
  }

  if (!destination) {
    return <GuideNotFound guide="destination" title="Destination not found" />;
  }

  tripsState.setPreviewPanelTitle(destination.displayName);

  const images = destination.imagesForGallery ?? [];

  const mapPoints = [{ id: '1', address: destination.name, label: destination.displayName }];

  return (
    <GuideLayout
      images={images}
      title={destination.displayName}
      metadata={
        <>
          {destination.location?.countryName && destination.location?.countryName !== destination.displayName && (
            <div className="flex items-center gap-1">
              <span>{getEmojiFlag(destination.location?.countryCode || '')}</span>
              <span>{destination.location?.countryName}</span>
            </div>
          )}
          {destination.logistics?.timeZone && (
            <div className="flex items-center gap-1">
              {formatTimeInTimezone(new Date().toISOString(), destination.logistics?.timeZone || '', 'h:mm a')}
            </div>
          )}
          {(() => {
            const weather = destinationGuideState.weather;
            if (!weather) return undefined;
            const icon = mapWeatherConditionToMaterialIcon(weather.condition, weather.description);
            return (
              <div className="flex items-center gap-1">
                <div className="flex items-center gap-1">
                  <MaterialIcon icon={icon as IconName} size={IconSizes.xs} /> {weather.condition}
                </div>
                <div>
                  {Math.round(weather.temperature)}°{weather.units === 'imperial' ? 'F' : 'C'}
                </div>
              </div>
            );
          })()}
        </>
      }
      actions={
        tripsState.current ? (
          <>
            <AskElaraButton
              onClick={() => {
                const id = destination.id || destinationId;
                if (id) {
                  chatInputState.addDataAttachment('destination', id, destination.displayName);

                  tripsState.switchToPanel('chat');
                  tripsState.closePreviewPanel();

                  chatInputState.focusInput();
                }
              }}
            />
            <ShortlistButton
              itemType="destination"
              referenceId={destination.id || destinationId!}
              title={destination.displayName}
            />
          </>
        ) : (
          // Opened outside a trip (e.g. from a Story): the actions above all need a current trip.
          <PlanTripCTA type="destination" id={destination.id || destinationId!} name={destination.displayName} />
        )
      }
    >
      <>
        {/* Long description and map */}
        <div className="grid grid-cols-1 @3xl:grid-cols-2 gap-8">
          <div className="flex flex-col items-start gap-4">
            {destination.longDescription && (
              <SimpleMarkdown
                text={destination.longDescription}
                className="text-pretty max-w-prose [&>p:first-child]:text-lg [&>p:first-child]:leading-[145%]"
              />
            )}
          </div>
          <div>
            <MapWidget points={mapPoints} size="full" className="my-0 min-h-96" />
          </div>
        </div>

        <div className="grid grid-cols-1 @3xl:grid-cols-2 gap-8 items-start">
          {/* Weather */}
          <Card>
            <CardHeader>
              <CardTitle>Weather</CardTitle>
            </CardHeader>
            <CardContent>
              {showWeatherForecast &&
              destinationGuideState.forecast &&
              destinationGuideState.forecast.length > 0 &&
              !destinationGuideState.weatherError ? (
                <WeatherForecast
                  forecast={destinationGuideState.forecast}
                  units={preferredUnits}
                  className="-mx-6 px-6 md:-mx-8 md:px-8 max-w-full box-content"
                />
              ) : (
                <div>{destination.weather_seasons && <SimpleMarkdown text={destination.weather_seasons} />}</div>
              )}
            </CardContent>
          </Card>

          {/* Good to know */}
          <Card className="p-0">
            <CardHeader>
              <CardTitle>Good to know</CardTitle>
            </CardHeader>
            <CardContent>
              <DestinationTips destination={destination} />
            </CardContent>
          </Card>
        </div>
      </>
    </GuideLayout>
  );
}
